import React, { useState, useEffect } from 'react';
import { resetTransferCounts } from '../utils/resetTransferCounts';
import { useAuth } from '../contexts/AuthContext';
import AdminRoute from '../components/AdminRoute';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, updateDoc, doc, query, where, getDoc, setDoc } from 'firebase/firestore';

const SETTINGS_REF = { collection: 'adminSettings', id: 'transferLimits' };

export default function AdminResetTransfers() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [users, setUsers] = useState([]);
  const [userCounts, setUserCounts] = useState({});
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [limitEnabled, setLimitEnabled] = useState(true);
  const [savingLimit, setSavingLimit] = useState(false);

  // Fetch admin setting and users with transfer counts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const settingsDoc = await getDoc(doc(db, SETTINGS_REF.collection, SETTINGS_REF.id));
        if (settingsDoc.exists()) {
          setLimitEnabled(settingsDoc.data().isEnabled !== false);
        }

        const usersRef = collection(db, 'users');
        const usersQuery = query(usersRef, where("role", "!=", "admin"));
        const querySnapshot = await getDocs(usersQuery);

        const usersData = querySnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        setUsers(usersData);

        const counts = {};
        for (const user of usersData) {
          const userDoc = await getDoc(doc(db, 'users', user.id));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            counts[user.id] = userData.transferCount || userData.transfers || userData.transfer_count || 0;
          }
        }
        setUserCounts(counts);
      } catch (error) {
        console.error('Error fetching data:', error);
        setResult({ success: false, error: 'Failed to fetch users' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleResetAll = async () => {
    setLoading(true);
    try {
      const response = await resetTransferCounts();
      setResult(response);
      // Refresh user counts after reset
      const counts = {};
      for (const user of users) {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          counts[user.id] = userData.transferCount || userData.transfers || userData.transfer_count || 0;
        }
      }
      setUserCounts(counts);
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    setLoading(false);
    setShowConfirmReset(false);
  };

  const handleToggleLimit = async () => {
    setSavingLimit(true);
    try {
      const newValue = !limitEnabled;
      await setDoc(doc(db, SETTINGS_REF.collection, SETTINGS_REF.id), {
        isEnabled: newValue,
        lastUpdated: new Date(),
        updatedBy: currentUser?.uid
      }, { merge: true });
      setLimitEnabled(newValue);
      setResult({ success: true, message: newValue ? 'Transfer count limit is now ON. Counts apply.' : 'Transfer count limit is now OFF. All transfers allowed.' });
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    setSavingLimit(false);
  };

  const handleResetUser = async (userId) => {
    setLoading(true);
    try {
      const user = users.find(u => u.id === userId);

      await updateDoc(doc(db, 'users', userId), {
        transferCount: 0,
        transfers: 0,
        transfer_count: 0
      });

      setUserCounts(prev => ({ ...prev, [userId]: 0 }));
      setResult({ success: true, message: `Reset transfer count for ${user?.email || userId}` });
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  return (
    <AdminRoute>
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Admin Dashboard
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Reset Transfer Counts</h2>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              {/* Transfer count limit on/off */}
              <div className="mb-8 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Transfer count limit</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {limitEnabled
                        ? 'On - users are limited by their transfer count. When switched on, existing counts apply.'
                        : 'Off - all transfers are allowed; counts are not checked or updated.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleLimit}
                    disabled={savingLimit}
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${limitEnabled ? 'bg-primary-600' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${limitEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </button>
                </div>
              </div>

              {/* Reset All Button */}
              <div className="mb-8">
                {!showConfirmReset ? (
                  <button
                    onClick={() => setShowConfirmReset(true)}
                    className="w-full py-3 px-4 border border-transparent rounded-xl shadow-sm text-white bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Reset all transfer counts
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                      <p className="text-yellow-700">Reset transfer counts for all users? This cannot be undone.</p>
                    </div>
                    <div className="flex space-x-4">
                      <button
                        onClick={handleResetAll}
                        disabled={loading}
                        className={`flex-1 py-3 px-4 border border-transparent rounded-xl shadow-sm text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {loading ? 'Resetting...' : 'Confirm reset all'}
                      </button>
                      <button
                        onClick={() => setShowConfirmReset(false)}
                        className="flex-1 py-3 px-4 border border-gray-300 rounded-xl shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <p className="mt-2 text-sm text-gray-600 text-center">
                  Resets transfer count to 0 for every user (including admins).
                </p>
              </div>

              {/* Users List */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">User Transfer Counts</h3>
                <div className="bg-gray-50 rounded-xl p-4">
                  {users.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.email}</p>
                        <p className="text-xs text-gray-500">Current count: {userCounts[user.id] || 0}</p>
                      </div>
                      <button
                        onClick={() => handleResetUser(user.id)}
                        disabled={loading}
                        className={`px-3 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Reset
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Result Message */}
              {result && (
                <div className={`
                  mt-4 p-4 rounded-xl
                  ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}
                `}>
                  {result.success ? (
                    <p>{result.message || `Successfully reset ${result.updatedCount} users. ${result.skippedCount} users skipped.`}</p>
                  ) : (
                    <p>Error: {result.error}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminRoute>
  );
} 